import { useState } from "react";
import { CheckCircle2, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";

const supportOptions = [
  "Supported Independent Living (SIL)",
  "In-Home Support",
  "Community Access",
  "Capacity Building",
  "Support Coordination",
  "Not sure yet",
];

const planTypes = ["NDIA-managed", "Plan-managed", "Self-managed", "Not sure"];

const initial = {
  participant_name: "",
  contact_name: "",
  email: "",
  phone: "",
  ndis_number: "",
  plan_type: "",
  support_types: [],
  preferred_times: "",
  message: "",
  consent: false,
};

export default function Referrals() {
  useSeo({
    title: "Referrals & Intake | Valora Support Services",
    description: "Refer yourself, a participant, or a client to Valora. Quick NDIS-friendly intake form — we respond within one business day.",
  });

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSupport = (s) => {
    setForm((f) => ({
      ...f,
      support_types: f.support_types.includes(s)
        ? f.support_types.filter((x) => x !== s)
        : [...f.support_types, s],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.participant_name.trim()) e.participant_name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.plan_type) e.plan_type = "Please select";
    if (!form.message.trim()) e.message = "Tell us a little about the support needs";
    if (!form.consent) e.consent = "Consent is required to contact you";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    try {
      setLoading(true);
      await api.post("/enquiries", form);
      setSuccess(true);
      setForm(initial);
      toast.success("Enquiry received — we'll be in touch within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="referrals-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-20">
          <p className="eyebrow">Referrals & Intake</p>
          <h1 className="mt-3 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#0F2A2C]">
            One short form. One friendly call. Real support, soon.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#4A5568]">
            Whether you're a participant, a family member, a carer, or a support coordinator — start here. We'll respond within one business day with next steps.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-white pb-20">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-6">
            <div className="rounded-2xl border border-[#e6efee] p-6 bg-[#F8FBFA]">
              <div className="flex items-center gap-2 text-[#0B6E70] font-semibold">
                <ShieldCheck className="h-5 w-5" /> Your information is safe
              </div>
              <p className="mt-2 text-sm text-[#4A5568]">We only use the details you share to contact you about supports. We never sell or share data.</p>
            </div>
            <div className="rounded-2xl border border-[#e6efee] p-6 bg-white">
              <p className="eyebrow">What happens next</p>
              <ol className="mt-3 space-y-2 text-sm text-[#0F2A2C]">
                <li><span className="font-semibold text-[#0B6E70]">1.</span> We review your enquiry.</li>
                <li><span className="font-semibold text-[#0B6E70]">2.</span> We call (or email) within one business day.</li>
                <li><span className="font-semibold text-[#0B6E70]">3.</span> We arrange a no-obligation chat about goals.</li>
                <li><span className="font-semibold text-[#0B6E70]">4.</span> If we're a good fit, we start supports.</li>
              </ol>
            </div>
            <div className="rounded-2xl border border-[#e6efee] p-6 bg-white">
              <p className="eyebrow">Prefer to talk?</p>
              <a href="tel:0426635885" className="mt-3 btn-secondary w-full justify-center" data-testid="referrals-call-btn">Call 0426 635 885</a>
              <a href="mailto:info@valorasupportservices.com" className="mt-3 inline-flex items-center justify-center w-full text-sm text-[#0B6E70] hover:underline" data-testid="referrals-email-link">
                info@valorasupportservices.com
              </a>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {success ? (
              <div data-testid="enquiry-success" className="rounded-3xl border border-[#7FD1CB] bg-[#E6FFFA] p-10 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-white flex items-center justify-center shadow">
                  <CheckCircle2 className="h-7 w-7 text-[#0B6E70]" />
                </div>
                <h2 className="mt-5 font-serif text-3xl text-[#0F2A2C] font-medium">Thank you — your enquiry is in.</h2>
                <p className="mt-3 text-[#0F2A2C]/80">A member of our intake team will be in touch within one business day. If it's urgent, please call <a href="tel:0426635885" className="font-semibold underline">0426 635 885</a>.</p>
                <button type="button" onClick={() => setSuccess(false)} data-testid="enquiry-new-btn" className="mt-7 btn-primary">
                  Submit another enquiry <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="rounded-3xl border border-[#e6efee] bg-white p-6 sm:p-10 space-y-7" data-testid="enquiry-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field" htmlFor="participant_name">Participant name *</label>
                    <input id="participant_name" data-testid="input-participant-name" className="input-field" value={form.participant_name} onChange={(e) => update("participant_name", e.target.value)} />
                    {errors.participant_name && <p className="mt-1 text-xs text-red-600">{errors.participant_name}</p>}
                  </div>
                  <div>
                    <label className="label-field" htmlFor="contact_name">Your name (if different)</label>
                    <input id="contact_name" data-testid="input-contact-name" className="input-field" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field" htmlFor="email">Email *</label>
                    <input id="email" type="email" data-testid="input-email" className="input-field" value={form.email} onChange={(e) => update("email", e.target.value)} />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="label-field" htmlFor="phone">Phone *</label>
                    <input id="phone" data-testid="input-phone" className="input-field" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                    {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field" htmlFor="ndis_number">NDIS number</label>
                    <input id="ndis_number" data-testid="input-ndis" className="input-field" value={form.ndis_number} onChange={(e) => update("ndis_number", e.target.value)} placeholder="e.g. 4300xxxxxx" />
                  </div>
                  <div>
                    <label className="label-field" htmlFor="plan_type">Plan type *</label>
                    <select id="plan_type" data-testid="select-plan-type" className="input-field" value={form.plan_type} onChange={(e) => update("plan_type", e.target.value)}>
                      <option value="">Select an option</option>
                      {planTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {errors.plan_type && <p className="mt-1 text-xs text-red-600">{errors.plan_type}</p>}
                  </div>
                </div>

                <div>
                  <label className="label-field">Supports you're interested in</label>
                  <div className="flex flex-wrap gap-2">
                    {supportOptions.map((s) => {
                      const active = form.support_types.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          data-testid={`support-chip-${s.replace(/\s+/g, '-').toLowerCase()}`}
                          onClick={() => toggleSupport(s)}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                            active ? "bg-[#0B6E70] text-white border-[#0B6E70]" : "bg-white text-[#0F2A2C] border-[#d6e1e0] hover:border-[#0B6E70]"
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="label-field" htmlFor="preferred_times">Preferred days / times</label>
                  <input id="preferred_times" data-testid="input-preferred-times" className="input-field" placeholder="e.g. Weekday mornings, or Saturday afternoons" value={form.preferred_times} onChange={(e) => update("preferred_times", e.target.value)} />
                </div>

                <div>
                  <label className="label-field" htmlFor="message">Tell us about the support needs *</label>
                  <textarea id="message" data-testid="input-message" rows={5} className="input-field resize-y" placeholder="A few sentences about goals, routines, current supports — anything that helps us understand." value={form.message} onChange={(e) => update("message", e.target.value)} />
                  {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                </div>

                <label className="flex items-start gap-3 cursor-pointer" htmlFor="consent">
                  <input id="consent" type="checkbox" data-testid="input-consent" className="mt-1 h-5 w-5 rounded border-[#d6e1e0] text-[#0B6E70] focus:ring-[#0B6E70]" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                  <span className="text-sm text-[#4A5568]">I consent to Valora Support Services contacting me about this enquiry. I understand my information will be handled in line with the Privacy Act 1988 (Cth).</span>
                </label>
                {errors.consent && <p className="-mt-4 text-xs text-red-600">{errors.consent}</p>}

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-2">
                  <button type="submit" disabled={loading} data-testid="enquiry-submit-btn" className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Submit enquiry <ArrowRight className="h-4 w-4" /></>}
                  </button>
                  <p className="text-xs text-[#4A5568]">We typically respond within one business day.</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
